/**
 * How a run status looks, in one place.
 *
 * The badge mapping lived inline in `StatusBadge` until the run rail needed a dot
 * for the same seven values. Two copies of a status→colour table is the failure
 * this file prevents: the rail and the run header would eventually disagree about
 * whether `blocked` is red, and nothing would say which one was right.
 *
 * The reasoning behind the colours, kept from `StatusBadge`:
 *
 * `running` is blue rather than yellow because a sweep running for hours is the
 * normal case here, not a warning.
 *
 * `empty` is yellow and must not be collapsed into either neighbour: the sweep ran
 * to completion and wrote no ranked target, so it is not a failure (nothing errored)
 * and not a success (there is nothing to show). A run in that state used to read
 * `succeeded` while the board below it showed its own empty state, which is
 * indistinguishable from "no runs yet".
 *
 * `blocked` is red, with `failed`, because it is a fault: no ranking *and* a data
 * source that stopped answering. It is the reason `empty` can be trusted to mean
 * what it says — the two were one status until a sweep wrote nothing because
 * Neptune was down, and the yellow badge invited reading a dead dependency as a
 * barren search space.
 */
import type { RunSummary } from '../api/types'

export type RunStatus = RunSummary['status']

export interface RunStatusLook {
  /** Variant for `ui/Badge`. */
  badge: 'default' | 'green' | 'blue' | 'yellow' | 'red'
  /** Background class for a 6px dot. Themed shades only. */
  dot: string
  /** Sentence-case, for the rail and for a dot's accessible name. */
  label: string
}

const KNOWN: Record<string, RunStatusLook> = {
  queued: { badge: 'yellow', dot: 'bg-yellow-400', label: 'Queued' },
  running: { badge: 'blue', dot: 'bg-blue-400', label: 'Running' },
  succeeded: { badge: 'green', dot: 'bg-brand-400', label: 'Succeeded' },
  empty: { badge: 'yellow', dot: 'bg-yellow-400', label: 'No targets ranked' },
  blocked: { badge: 'red', dot: 'bg-red-400', label: 'Blocked' },
  failed: { badge: 'red', dot: 'bg-red-400', label: 'Failed' },
  cancelled: { badge: 'default', dot: 'bg-slate-500', label: 'Cancelled' },
  // The graph raised, but a cleared pool was recovered and written after the
  // fact (`api/routes/agentcore.py`'s salvage path). Yellow, with `empty`: a
  // ranking exists and is real, but the run did not finish cleanly, so this is
  // neither the green of `succeeded` nor the red of a `failed` run with nothing
  // to show for it.
  salvaged: { badge: 'yellow', dot: 'bg-yellow-400', label: 'Salvaged' },
}

/**
 * The look for a status, falling back to **neutral and the raw value** — never to
 * `succeeded`.
 *
 * `RunSummary['status']` is a union with `| string` on the end because the harness
 * can write a status this UI has not been taught, so this fallback is a real path
 * and not defensive padding. Defaulting it to a success would render a sweep whose
 * data sources stopped answering as a finished one; showing the raw value instead
 * is honest and is also how the next unknown status gets noticed.
 */
export function runStatusLook(status: string | null | undefined): RunStatusLook {
  if (!status) return { badge: 'default', dot: 'bg-slate-500', label: 'Unknown' }
  return (
    KNOWN[status] ?? { badge: 'default', dot: 'bg-slate-500', label: status }
  )
}

/** The statuses this UI has copy for. Exported for the fixture API and tests. */
export const KNOWN_RUN_STATUSES = Object.keys(KNOWN)
